import { useCallback } from 'react';
import axios from 'axios';

const DEFAULT_LOCAL_SERVICE_URL = 'http://localhost:3003';

export interface CashDrawerTriggerOptions {
  /**
   * 'auto' (default) -> backend hem pin 2 hem pin 5 dener.
   * 'pin2' / 0 / 2   -> sadece pin 2 (XPrinter, Epson varsayilani).
   * 'pin5' / 1 / 5   -> sadece pin 5 (Afanda/Aafnet, bazi Hisense modelleri).
   */
  pin?: 'auto' | 'pin2' | 'pin5' | 0 | 1 | 2 | 5;
  /** Pulse ON suresi (1..255, ~2ms birim). Default 25 = 50ms. */
  pulseOn?: number;
  /** Pulse OFF suresi (1..255, ~2ms birim). Default 250 = 500ms. */
  pulseOff?: number;
}

/**
 * Para çekmecesi tetikleyicisi için hook.
 *
 * local-service üzerindeki `/api/print/open-cash-drawer` endpoint'ine
 * ESC/POS "ESC p m t1 t2" komutunu yollar. Backend default'ta hem pin 2 hem
 * pin 5 dener (bazı Çin menşeli yazıcılarda — Afanda gibi — çekmece pin 5'e
 * bağlıdır ve standart komut tetiklemez).
 *
 * Kullanım:
 *   const openCashDrawer = useOpenCashDrawer(screenSettings?.localServiceUrl);
 *   openCashDrawer('XP-80C'); // fire-and-forget, auto-pin
 *   openCashDrawer('AFANDA', { pin: 'pin5' }); // sadece pin 5
 *
 * Ödeme akışını BLOKLAMAZ; hata olursa sadece console'a yazar.
 */
export function useOpenCashDrawer(localServiceUrl?: string | null) {
  const baseUrl = localServiceUrl || DEFAULT_LOCAL_SERVICE_URL;

  return useCallback(
    async (
      printerName: string | null | undefined,
      options?: CashDrawerTriggerOptions
    ): Promise<boolean> => {
      if (!printerName) {
        console.warn('[CashDrawer] printerName boş, tetikleme atlandı.');
        return false;
      }

      const url = `${baseUrl}/api/print/open-cash-drawer`;
      const payload: Record<string, unknown> = { printerName };
      if (options?.pin !== undefined) payload.pin = options.pin;
      if (options?.pulseOn !== undefined) payload.pulseOn = options.pulseOn;
      if (options?.pulseOff !== undefined) payload.pulseOff = options.pulseOff;

      const started = performance.now();
      console.log(
        `%c💰 [CashDrawer] → POST ${url}`,
        'color: #2e7d32; font-weight: bold;',
        payload
      );
      try {
        const response = await axios.post(url, payload, { timeout: 5000 });
        const elapsed = Math.round(performance.now() - started);
        const data = response.data || {};
        const success = data.success !== false;

        if (success) {
          console.log(
            `%c💰 [CashDrawer] ✓ OK (${elapsed}ms): ${data.message || '(mesaj yok)'}`,
            'color: #2e7d32;'
          );
        } else {
          console.warn(
            `💰 [CashDrawer] ✗ backend success=false (${elapsed}ms):`,
            data
          );
        }
        if (Array.isArray(data.details) && data.details.length > 0) {
          // Tablo halinde hangi pin'in başarılı olduğunu göster.
          // (devtools'ta filtrelemek kolay olsun diye console.table)
          // eslint-disable-next-line no-console
          console.table(data.details);
        }
        return success;
      } catch (err: any) {
        const elapsed = Math.round(performance.now() - started);
        const status = err?.response?.status;
        const respBody = err?.response?.data;
        console.error(
          `💰 [CashDrawer] ✗ Tetik hatası (${elapsed}ms) status=${status}:`,
          err?.message || err
        );
        if (respBody) {
          console.error('💰 [CashDrawer]   backend body:', respBody);
        }
        if (err?.code) {
          console.error(`💰 [CashDrawer]   axios code=${err.code}`);
        }
        return false;
      }
    },
    [baseUrl]
  );
}

export default useOpenCashDrawer;
